import React, { useState, useEffect } from "react";
import { FaTimes, FaCamera, FaPaperPlane, FaExclamationTriangle } from "react-icons/fa";
import imageCompression from "browser-image-compression";

const ComplaintModal = ({ isOpen, onClose, establishmentId, backEnd_request, establishmentName, verifiedPhone, phoneToken, onTokenInvalid }) => {
    const [formData, setFormData] = useState({
        complaintText: "",
        phoneNumber: verifiedPhone || "",
        // wantsTracking: true,
        phoneToken: phoneToken || ""
    });

    const [selectedPhotos, setSelectedPhotos] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState({ type: "", msg: "" });

    // مزامنة البيانات عند تحديث الهاتف أو التوكن من المكون الأب
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            phoneNumber: verifiedPhone || prev.phoneNumber,
            phoneToken: phoneToken || prev.phoneToken
        }));
    }, [verifiedPhone, phoneToken]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (selectedPhotos.length + files.length > 2) {
            setStatus({ type: "danger", msg: "عذراً، الحد الأقصى هو صورتين فقط." });
            return;
        }
        // قبول ملفات الصور فقط وتحويلها لاحقاً لـ JPG
        const validFiles = files.filter(file => file.type.startsWith("image/"));
        setSelectedPhotos(prev => [...prev, ...validFiles]);
    };

    const removePhoto = (index) => {
        setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // التأكد من وجود توكن التحقق
        if (!formData.phoneToken) {
            setStatus({ type: "danger", msg: "جلسة التحقق غير صالحة، يرجى إعادة التحقق من الهاتف." });
            if (onTokenInvalid) setTimeout(() => onTokenInvalid(), 2000);
            return;
        }

        setSubmitting(true);
        setStatus({ type: "info", msg: "جاري معالجة الصور وإرسال الشكوى..." });

        const data = new FormData();
        // الأسماء البرمجية حسب الـ Backend DTO
        data.append("EstablishmentFieldUid", establishmentId);
        data.append("ComplaintText", formData.complaintText);
        data.append("PhoneNumber", formData.phoneNumber);
        // data.append("WantsTracking", formData.wantsTracking);
        data.append("PhoneToken", formData.phoneToken);

        try {
            const compressionOptions = {
                maxSizeMB: 0.8,
                maxWidthOrHeight: 1200,
                useWebWorker: true,
                fileType: "image/jpeg"
            };

            for (const file of selectedPhotos) {
                const compressedFile = await imageCompression(file, compressionOptions);
                // تأكيد لاحقة الملف لتكون .jpg دائماً كما يطلب الباك إيند
                const finalFile = new File(
                    [compressedFile],
                    file.name.replace(/\.[^/.]+$/, "") + ".jpg",
                    { type: "image/jpeg" }
                );
                data.append("Photos", finalFile);
            }

            await backEnd_request.post("/public/complaints", data, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            setStatus({ type: "success", msg: "تم إرسال شكواك بنجاح. سنقوم بمتابعتها." });

            setTimeout(() => {
                setFormData({ complaintText: "", phoneNumber: verifiedPhone, phoneToken: phoneToken });
                setSelectedPhotos([]);
                setStatus({ type: "", msg: "" });
                onClose();
            }, 2500);

        } catch (error) {
            // جلب رسالة الخطأ من الباك إيند بشكل ديناميكي
            const errorMsg = error.response?.data?.message || "فشل إرسال الشكوى، يرجى المحاولة لاحقاً.";
            setStatus({ type: "danger", msg: errorMsg });

            // إذا كان التوكن منتهي الصلاحية (401)
            if (error.response?.status === 401 && onTokenInvalid) {
                setTimeout(() => onTokenInvalid(), 2000);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-glass-card shadow-lg animate-fade-in" onClick={e => e.stopPropagation()}>
                <div className="modal-header-custom border-bottom pb-3 mb-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <div className="icon-badge-red"><FaExclamationTriangle /></div>
                        <div>
                            <h5 className="fw-bold mb-0 text-dark">تقديم شكوى رسمية</h5>
                            <small className="text-muted">المنشأة: {establishmentName || "غير محدد"}</small>
                        </div>
                    </div>
                    <button className="btn-close-circle" onClick={onClose}><FaTimes /></button>
                </div>

                {status.msg && (
                    <div className={`alert alert-${status.type} py-2 small border-0 shadow-sm mb-3 text-end animate-fade-in`} dir="rtl">
                        {submitting && <span className="spinner-border spinner-border-sm ms-2"></span>}
                        {status.msg}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="text-end" dir="rtl">
                    <div className="mb-2">
                        <label className="form-label small fw-bold text-muted">تفاصيل الشكوى *</label>
                        <textarea
                            name="complaintText"
                            className="form-control bg-light border-0 shadow-none"
                            rows="4"
                            placeholder=" اكتب بشكل ملخص تفاصيل الشكوى   ..."
                            required
                            value={formData.complaintText}
                            onChange={handleChange}
                            maxLength={500}
                        ></textarea>
                    </div>

                    <div className="d-flex justify-content-between mt-1 mb-3 px-1">
                        <small className="text-muted" style={{ fontSize: '10px' }}>الحد الأقصى 500 حرف</small>
                        <small
                            className={`${formData.complaintText.length >= 500 ? 'text-danger fw-bold' : 'text-muted'}`}
                            style={{ fontSize: '11px' }}
                            dir="ltr"
                        >
                            {formData.complaintText.length} / 500
                        </small>
                    </div>

                    <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <label className="upload-btn-custom">
                                <FaCamera className="ms-2" /> إرفاق صور إثبات (اختياري)
                                <input type="file" multiple hidden onChange={handleFileChange} accept="image/*" />
                            </label>
                            <small className="text-muted">{selectedPhotos.length} / 2</small>
                        </div>
                        <div className="d-flex flex-wrap gap-2 justify-content-start">
                            {selectedPhotos.map((file, index) => (
                                <div key={index} className="photo-chip">
                                    <small className="text-truncate" style={{ maxWidth: '100px' }}>{file.name}</small>
                                    <FaTimes className="cursor-pointer" onClick={() => removePhoto(index)} />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label small fw-bold text-muted">رقم الهاتف المرتبط بالشكوى</label>
                        <input
                            name="phoneNumber"
                            type="tel"
                            className="form-control bg-light border-0 shadow-none text-muted"
                            value={formData.phoneNumber}
                            readOnly
                        />
                        <small className="text-success fw-bold" style={{ fontSize: '10px' }}>✓ تم التحقق من الرقم عبر OTP</small>
                    </div>

                    {/* <div className="tracking-box mb-4 text-start">
                        <div className="form-check d-flex align-items-center gap-2 justify-content-end">
                            <label className="form-check-label small fw-bold text-primary order-1" htmlFor="trackCheck">تفعيل متابعة الشكوى</label>
                            <input className="form-check-input m-0 order-2" type="checkbox" name="wantsTracking" id="trackCheck" checked={formData.wantsTracking} onChange={handleChange} />
                        </div>
                        <div className="text-muted text-end mt-1" style={{ fontSize: '11px' }}>سنقوم بإرسال تحديثات الحالة إلى رقم هاتفك المحقق.</div>
                    </div> */}

                    <button type="submit" className="btn btn-danger w-100 fw-bold py-2 shadow-sm rounded-pill" disabled={submitting}>
                        {submitting ? "جاري الإرسال..." : <><FaPaperPlane className="ms-2" /> إرسال الشكوى الآن</>}
                    </button>
                </form>
            </div>

            <style>{`
                .modal-overlay { position: fixed; top:0; left:0; width:100%; height:100%; background: rgba(15, 23, 42, 0.85); backdrop-filter: blur(8px); display: flex; justify-content: center; align-items: center; z-index: 2000; }
                .modal-glass-card { background: white; width: 92%; max-width: 420px; border-radius: 25px; padding: 25px; position: relative; }
                .icon-badge-red { width: 45px; height: 45px; background: #fee2e2; color: #dc2626; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; }
                .btn-close-circle { border: none; background: #f1f5f9; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; color: #64748b; transition: 0.2s; }
                .btn-close-circle:hover { background: #e2e8f0; color: #1e293b; }
                .upload-btn-custom { cursor: pointer; background: #f8fafc; padding: 8px 15px; border-radius: 10px; border: 1px dashed #cbd5e1; font-size: 13px; color: #475569; transition: 0.2s; }
                .upload-btn-custom:hover { background: #f1f5f9; }
                .photo-chip { background: #eff6ff; color: #2563eb; padding: 4px 10px; border-radius: 8px; font-size: 11px; display: flex; align-items: center; gap: 8px; border: 1px solid #dbeafe; }
                .tracking-box { background: #f0f9ff; padding: 12px; border-radius: 15px; border: 1px solid #e0f2fe; }
                .form-control { border-radius: 12px !important; font-size: 14px; padding: 12px; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out; }
                @keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
                .cursor-pointer { cursor: pointer; }
            `}</style>
        </div>
    );
};

export default ComplaintModal;